/* ===========================
Utilities / fallbacks
=========================== */
if (typeof window.debounce !== 'function') {
  window.debounce = function (fn, wait = 200) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
}

/* ===========================
  Cart remove button
  =========================== */
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => {
      event.preventDefault();
      const cartItems =
        this.closest('cart-drawer-items') ||
        this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

/* ===========================
  Progress bar
  =========================== */
class ProgressBar extends HTMLElement {
  constructor() {
    super();
    this.progress = this.getAttribute('data-progress');
  }
  connectedCallback() {
    setTimeout(() => {
      const line = this.querySelector('[data-progress-line]');
      if (line) line.style.width = this.progress;
    }, 100);
  }
}
customElements.define('progress-bar', ProgressBar);

/* ===========================
  Cart items
  =========================== */
class CartItems extends HTMLElement {
  constructor() {
    super();
    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce(event => this.onChange(event), 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));
  }

  onChange(event) {
    if (event.target.name !== 'updates[]') return;
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement && document.activeElement.getAttribute('name')
    );
  }

  getSectionsToRender() {
    const main = document.getElementById('main-cart-items');
    const mainId = main ? main.dataset.id : '';
    return [
      {
        id: `#shopify-section-${mainId}`,
        section: mainId,
        selector: `#shopify-section-${mainId} cart-items`
      },
      {
        id: '#cart-counter',
        section: 'cart-counter',
        selector: '#shopify-section-cart-counter'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.classList.add('is-loading');

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map(s => s.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body }
    })
      .then(response => response.text())
      .then(state => {
        const parsedState = JSON.parse(state);

        this.getSectionsToRender().forEach(section => {
          const elementToReplace =
            section.id === '#cart-counter'
              ? document.querySelector(section.id)
              : document.querySelector(section.selector);

          if (!elementToReplace) return;

          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        // After sections swap, (re)attach observers and update CSS height vars
        requestAnimationFrame(() => {
          attachCartResizeObserver();
          setCartDrawerHeights();
        });

        this.updateLiveRegions(line, parsedState.item_count);
        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          lineItem.querySelector(`[name="${name}"]`).focus();
        }
      })
      .finally(() => this.classList.remove('is-loading'));
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      document
        .querySelectorAll(`[data-line-item-error][data-line="${line}"]`)
        .forEach(error => {
          const qtyEl = document.querySelector(`[id*="ProductQuantity-${line}"]`);
          const qtyVal = qtyEl ? qtyEl.value : '';
          error.innerHTML = window.cartStrings.quantityError.replace('[quantity]', qtyVal);
        });
    }
    this.currentItemCount = itemCount;
  }
}
customElements.define('cart-items', CartItems);

/* ===========================
  Drawer height CSS vars
  =========================== */

// Prefer scoping to the drawer if present, else root
const getVarRoot = () =>
  document.querySelector('cart-drawer') || document.documentElement;

const setCartDrawerHeights = () => {
  const headerEl  = document.querySelector('[data-content="cart-drawer-title"]');
  const summaryEl = document.querySelector('[data-content="cart-summary-panel"]');

  const headerH  = headerEl  ? Math.ceil(headerEl.getBoundingClientRect().height)  : 0;
  const summaryH = summaryEl ? Math.ceil(summaryEl.getBoundingClientRect().height) : 0;

  const root = document.querySelector('cart-drawer') || getVarRoot();
  root.style.setProperty('--cart-drawer-header-height',  `${headerH}px`);
  root.style.setProperty('--cart-drawer-summary-height', `${summaryH}px`);
};

// Observe natural content growth/shrink (text wrap, progress bar, etc.)
let cartResizeObserver;
const attachCartResizeObserver = () => {
  const headerEl  = document.querySelector('[data-content="cart-drawer-title"]');
  const summaryEl = document.querySelector('[data-content="cart-summary-panel"]');

  if (cartResizeObserver) cartResizeObserver.disconnect();

  cartResizeObserver = new ResizeObserver(() => setCartDrawerHeights());
  if (headerEl)  cartResizeObserver.observe(headerEl);
  if (summaryEl) cartResizeObserver.observe(summaryEl);

  // If images/webfonts change layout after load, measure again
  onMediaSettled(headerEl);
  onMediaSettled(summaryEl);
};

const onMediaSettled = (node) => {
  if (!node) return;
  const imgs = Array.from(node.querySelectorAll('img'));
  let remaining = imgs.length;
  if (!remaining) return;
  imgs.forEach(img => {
    if (img.complete) return;
    img.addEventListener('load', () => {
      remaining--;
      if (remaining === 0) setCartDrawerHeights();
    }, { once: true });
  });
};

// Debounced window resize -> recompute
window.addEventListener('resize', debounce(() => {
  requestAnimationFrame(setCartDrawerHeights);
}, 150));

// If your theme emits an event when opening the drawer, recompute then too
window.addEventListener('cart-drawer:open', () => {
  requestAnimationFrame(() => {
    attachCartResizeObserver();
    setCartDrawerHeights();
  });
});

// First paint
window.addEventListener('DOMContentLoaded', () => {
  attachCartResizeObserver();
  setCartDrawerHeights();
});

// In case markup is swapped outside updateQuantity (open/close, section renders, etc.)
const safeSetHeights = debounce(() => {
  attachCartResizeObserver();
  setCartDrawerHeights();
}, 50);

const mo = new MutationObserver(() => {
  if (
    document.querySelector('[data-content="cart-drawer-title"]') ||
    document.querySelector('[data-content="cart-summary-panel"]')
  ) {
    safeSetHeights();
  }
});
mo.observe(document.documentElement, { childList: true, subtree: true });

const cartSwiper = new Swiper('[data-id="cart-upsell-swiper"]', {
  loop: true, // optional, makes slides loop
  slidesPerView: 1.33,
  centeredSlides: true,
  threshold: 6,
  pagination: {
    el: '#cart-upsell-pagination',
    clickable: true,
  },
  navigation: {
    nextEl: ".swiper-button-next",
    prevEl: ".swiper-button-prev",
  },
});
